import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Container,
    FormControl,
    TextField,
    Typography,
    Grid,
    Button,
    FormHelperText,
    FormGroup,
    FormControlLabel,
    Checkbox,
    makeStyles,
    TextareaAutosize
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
    TITLES,
    GENDERS,
    PREFERRED_PHONE_COUNTRIES,
    REFERRAL_SOURCES_TYPE,
    NOTIFICATION_TYPES
} from '../../collums-constants/index';
import { KeyboardDatePicker } from '@material-ui/pickers';
import Moment from 'moment/moment';
import ListApi from '../../collums-components/api/ListApi';
import { isValidPhone } from '../../collums-components/helpers/validPhoneNumber';
import { getOrganisationCountryCode } from '../../collums-components/helpers';
import { useSelector } from 'react-redux';
import PhoneNumberInput from 'material-ui-phone-number';
import CustomerApi from '../../api/customerApi';
import { toastr } from 'react-redux-toastr';
import { leadFormStyles } from './styles';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const titles = [TITLES.MRS, TITLES.MISS, TITLES.MS, TITLES.MR, TITLES.MX, TITLES.DR];
const genders = [GENDERS.MALE, GENDERS.FEMALE, GENDERS.TRANS_MALE, GENDERS.TRANS_FEMALE];

function Lead() {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const [referralSourceType, setReferralSourceType] = useState(Object.values(REFERRAL_SOURCES_TYPE).sort());
    const [errors, setErrors] = useState({});
    const [settings, setSettings] = useState({});
    const organisation = useSelector(state => state.auth.organisation);
    const [leadSaved, setLeadSaved] = useState(false);
    const leadSavedRef = useRef(leadSaved);
    const containerRef = useRef(null);

    const [isInvalid, setIsInvalid] = useState({
        otherPhone: false,
        mobilePhone: false
    });
    const decoded = queryParams.get('form') ? atob(queryParams.get('form')) : '';
    const isPreview = queryParams.get('setup');
    const params = new URLSearchParams(decoded);
    document.getElementById('root').style.backgroundColor = params.get('bc') || 'white';
    const font = params.get('font') || 'Inter, sans-serif';
    const fontColor = params.get('tc') || 'black';
    const backgroundColor = params.get('bc') || 'white';
    document.getElementById('root').style.color = fontColor;
    const buttonStyles = {
        backgroundColor: params.get('btnc') || '',
        color: params.get('btntc'),
        fontFamily: font
    };

    useEffect(() => {
        leadSavedRef.current = leadSaved;
    }, [leadSaved]);

    useEffect(() => {
        const element = containerRef.current;

        if (element) {
            const resizeIframe = () => {
                if (!leadSavedRef.current) {
                    const additionalHeight = isPreview ? 5 : 0;
                    const height = element.offsetHeight + additionalHeight;
                    window.parent.postMessage({ type: 'resize', height }, '*');
                }
            };

            const resizeObserver = new ResizeObserver(() => {
                resizeIframe();
            });

            resizeObserver.observe(element);
            window.addEventListener('resize', resizeIframe);

            return () => {
                resizeObserver.disconnect();
                window.removeEventListener('resize', resizeIframe);
            };
        }
        //eslint-disable-next-line
    }, []);

    const classes = makeStyles(leadFormStyles(fontColor, font))();

    const loadSettings = () => {
        const fields = (params.get('fields') || '').split(',');
        let interestedOptions = [];
        if (fields.includes('interested') && params.get('interested')) {
            interestedOptions = params.get('interested').split(',');
        }

        setSettings({
            fields: fields,
            required: (params.get('required') || '').split(','),
            title: params.get('title'),
            interestedTitle: params.get('interestedTitle') || 'Treatments interested in',
            interestedOptions: interestedOptions,
            footerText: params.get('footerText') || '',
            marketingText: params.get('marketingText') || 'How should we contact you?',
            thankYouText:
                params.get('thankYouText') ||
                '<p>Thank you for your enquiry,<br/>we&apos;ll be in touch as soon as we can</p>',
            redirectUrl: params.get('redirectUrl') || null,
            font: params.get('font') || null,
            messageTitle: params.get('messageTitle') || ''
        });
    };

    const [formData, setFormData] = useState({
        title: '',
        firstName: '',
        surname: '',
        email: '',
        interested: [],
        marketingTypes: []
    });

    useEffect(() => {
        loadSettings();
        ListApi.getAll().then(lists => {
            const referralSource = lists.find(el => el.sys_name === 'client_referral_sources');
            if (referralSource) {
                setReferralSourceType(referralSource.values.split('\n'));
            }
        });

        // Load reCAPTCHA v3 script
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
        //eslint-disable-next-line
    }, []);

    const handleChange = e => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setErrors({ ...errors, [e.target.name]: '' });
    };

    const handleSubmit = async e => {
        e.preventDefault();

        const newErrors = {};
        if (settings.fields.includes('title') && settings.required.includes('title') && !formData.title) {
            newErrors.title = 'Select title';
        }
        if (!formData.firstName) {
            newErrors.firstName = 'First name is required';
        }
        if (!formData.surname) {
            newErrors.surname = 'Surname is required';
        }
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (settings.fields.includes('gender') && settings.required.includes('gender') && !formData.gender) {
            newErrors.gender = 'Gender is required';
        }
        if (
            settings.fields.includes('mobilePhone') &&
            settings.required.includes('mobilePhone') &&
            !formData.mobilePhone
        ) {
            newErrors.mobilePhone = 'Phone is required';
        }
        if (
            settings.fields.includes('dateOfBirth') &&
            settings.required.includes('dateOfBirth') &&
            !formData.dateOfBirth
        ) {
            newErrors.dateOfBirth = 'Date of birth is required';
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            if (isPreview) {
                setLeadSaved(true);
                return;
            }

            let captchaToken;
            try {
                captchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit_lead' });
            } catch (err) {
                toastr.error('reCAPTCHA verification failed. Please try again.');
                return;
            }

            const lead = await CustomerApi.createLead({ ...formData, captchaToken });
            if (lead) {
                if (settings.redirectUrl) {
                    window.parent.postMessage(`redirect:${settings.redirectUrl}`, '*');
                    return true;
                }
                setLeadSaved(true);
            } else {
                toastr.error("Sorry, your lead couldn't be created.");
            }
        }
    };

    const getCountryCode = () => {
        return getOrganisationCountryCode({
            ...organisation,
            address: {
                country: organisation?.country || undefined
            }
        });
    };

    const renderTitleField = () => {
        return (
            <Grid item xs={6}>
                <Autocomplete
                    options={titles}
                    inputValue={formData.title || ''}
                    onInputChange={(event, value) => {
                        setFormData({ ...formData, title: value });
                        setErrors({ ...errors, title: '' });
                    }}
                    renderOption={option => <Typography className={classes.fontFamily}>{option}</Typography>}
                    classes={{ popupIndicator: classes.popupIndicator }}
                    autoHighlight
                    renderInput={params => (
                        <TextField
                            {...params}
                            label="Title"
                            variant="outlined"
                            error={Boolean(errors.title)}
                            helperText={errors.title}
                            required={settings.fields.includes('title') && settings.required.includes('title')}
                            InputProps={{
                                ...params.InputProps,
                                classes: {
                                    root: classes.outlinedInput,
                                    endAdornment: classes.dropdownIcon,
                                    input: `${classes.inputText} ${classes.fontFamily}`
                                },
                                style: { paddingTop: 0, paddingBottom: 0, fontSize: 16 }
                            }}
                            InputLabelProps={{
                                classes: { root: classes.fontFamily },
                                style: { color: fontColor }
                            }}
                        />
                    )}
                />
            </Grid>
        );
    };

    const renderFirstNameField = () => {
        return (
            <Grid item xs={6}>
                <FormControl fullWidth error={Boolean(errors.firstName)}>
                    <TextField
                        label="First name"
                        name="firstName"
                        value={formData.firstName}
                        error={Boolean(errors.firstName)}
                        required
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            classes: {
                                root: classes.outlinedInput,
                                input: `${classes.inputText} ${classes.fontFamily}`
                            }
                        }}
                        InputLabelProps={{
                            classes: {
                                root: classes.inputLabel
                            },
                            style: { fontFamily: font }
                        }}
                    />
                    {errors.firstName && (
                        <FormHelperText style={{ fontFamily: font }}>{errors.firstName}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };

    const renderSurnameField = () => {
        return (
            <Grid item xs={6}>
                <FormControl fullWidth error={Boolean(errors.surname)}>
                    <TextField
                        label="Last name"
                        name="surname"
                        required
                        value={formData.surname}
                        error={Boolean(errors.surname)}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            classes: {
                                root: classes.outlinedInput,
                                input: `${classes.inputText} ${classes.fontFamily}`
                            }
                        }}
                        InputLabelProps={{
                            classes: {
                                root: classes.inputLabel
                            },
                            style: { fontFamily: font }
                        }}
                    />
                    {errors.surname && <FormHelperText style={{ fontFamily: font }}>{errors.surname}</FormHelperText>}
                </FormControl>
            </Grid>
        );
    };

    const renderEmailField = () => {
        return (
            <Grid item xs={6}>
                <FormControl fullWidth error={Boolean(errors.email)}>
                    <TextField
                        label="Email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        error={Boolean(errors.email)}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            classes: {
                                root: classes.outlinedInput,
                                input: `${classes.inputText} ${classes.fontFamily}`
                            }
                        }}
                        InputLabelProps={{
                            classes: {
                                root: classes.inputLabel
                            },
                            style: { fontFamily: font }
                        }}
                    />
                    {errors.email && <FormHelperText style={{ fontFamily: font }}>{errors.email}</FormHelperText>}
                </FormControl>
            </Grid>
        );
    };

    const renderGenderField = () => {
        return (
            <Grid item xs={6}>
                <Autocomplete
                    options={genders}
                    defaultValue={formData.gender || ''}
                    onInputChange={(event, value) => setFormData({ ...formData, gender: value })}
                    classes={{ popupIndicator: classes.popupIndicator }}
                    renderOption={option => <Typography className={classes.fontFamily}>{option}</Typography>}
                    renderInput={params => (
                        <TextField
                            {...params}
                            label="Gender"
                            variant="outlined"
                            error={Boolean(errors.gender)}
                            required={settings.fields.includes('gender') && settings.required.includes('gender')}
                            InputProps={{
                                ...params.InputProps,
                                classes: {
                                    root: classes.outlinedInput,
                                    endAdornment: classes.dropdownIcon,
                                    input: `${classes.inputText} ${classes.fontFamily}`
                                },
                                style: { paddingTop: 0, paddingBottom: 0, fontSize: 16 } // bez fontFamily, bo klasa
                            }}
                            InputLabelProps={{
                                classes: {
                                    root: classes.inputLabel
                                }
                            }}
                        />
                    )}
                />
            </Grid>
        );
    };

    const renderDateOfBirthField = () => {
        return (
            <Grid item xs={6}>
                <FormControl fullWidth error={Boolean(errors.dateOfBirth)}>
                    <KeyboardDatePicker
                        inputVariant="outlined"
                        fullWidth
                        value={formData.dateOfBirth || null}
                        label="Dob"
                        format="dd/MM/yyyy"
                        placeholder="dd/mm/yyyy"
                        required={settings.fields.includes('dateOfBirth') && settings.required.includes('dateOfBirth')}
                        onChange={event => {
                            if (!event) return;
                            const date = Moment(event).format('YYYY-MM-DD') + 'T12:00:00.00Z';
                            setFormData({ ...formData, dateOfBirth: date });
                        }}
                        error={Boolean(errors.dateOfBirth)}
                        helperText={errors.dateOfBirth}
                        className={classes.datePickerInput}
                        keyboardIcon={null}
                        InputProps={{
                            style: { paddingTop: 0, paddingBottom: 0, fontSize: 16, color: fontColor },
                            classes: { input: classes.fontFamily }
                        }}
                        InputLabelProps={{
                            classes: { root: classes.inputLabel }
                        }}
                    />
                </FormControl>
            </Grid>
        );
    };

    const renderMobilePhoneField = () => {
        return (
            <Grid item xs={6}>
                <FormControl fullWidth error={Boolean(errors.mobilePhone) || isInvalid.mobilePhone}>
                    <PhoneNumberInput
                        fullWidth
                        variant="outlined"
                        value={formData.mobilePhone || ''}
                        label="Mobile"
                        required={settings.fields.includes('mobilePhone') && settings.required.includes('mobilePhone')}
                        enableLongNumbers={true}
                        onChange={(mobile, phoneInfo) => {
                            const isInvalidNow = !isValidPhone(mobile, phoneInfo.countryCode, phoneInfo.dialCode);
                            setIsInvalid(prev => ({
                                ...prev,
                                mobilePhone: isInvalidNow
                            }));
                            setFormData({ ...formData, mobilePhone: mobile });
                            if (!isInvalidNow) {
                                setErrors(prev => ({ ...prev, mobilePhone: '' }));
                            }
                        }}
                        error={Boolean(errors.mobilePhone) || isInvalid.mobilePhone}
                        preferredCountries={PREFERRED_PHONE_COUNTRIES}
                        defaultCountry={getCountryCode()}
                        InputProps={{
                            ...params.InputProps,
                            classes: {
                                root: classes.outlinedInput,
                                input: classes.fontFamily
                            },
                            style: { color: fontColor }
                        }}
                        InputLabelProps={{
                            classes: { root: classes.inputLabel }
                        }}
                    />
                    {(errors.mobilePhone || isInvalid.mobilePhone) && (
                        <FormHelperText className={classes.fontFamily}>
                            {errors.mobilePhone || 'Enter a valid mobile number'}
                        </FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };

    const renderReferralSourceField = () => {
        return (
            <Grid item xs={12}>
                <FormControl fullWidth>
                    <Autocomplete
                        ListboxProps={{ id: 'listbox' }}
                        getOptionLabel={option => option && option}
                        options={referralSourceType}
                        autoHighlight
                        inputValue={formData.referralSource}
                        onInputChange={event => setFormData({ ...formData, referralSource: event.target?.textContent })}
                        renderOption={option => <Typography className={classes.fontFamily}>{option}</Typography>}
                        classes={{ popupIndicator: classes.popupIndicator }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                InputProps={{
                                    ...params.InputProps,
                                    classes: {
                                        root: classes.outlinedInput,
                                        endAdornment: classes.dropdownIcon,
                                        input: classes.fontFamily
                                    }
                                }}
                                InputLabelProps={{
                                    classes: { root: classes.inputLabel }
                                }}
                                label="How did you hear about us?"
                                variant="outlined"
                            />
                        )}
                    />
                </FormControl>
            </Grid>
        );
    };

    const renderInterestedField = () => {
        return (
            <Grid item xs={12}>
                <FormControl component="fieldset">
                    <Typography variant="subtitle1" className={classes.fontFamily}>
                        {settings.interestedTitle}
                    </Typography>
                    <Grid container spacing={1}>
                        {settings.interestedOptions.map(option => (
                            <Grid item xs="auto" key={option}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.interested.includes(option)}
                                            onChange={e => {
                                                const newInterests = e.target.checked
                                                    ? [...formData.interested, option]
                                                    : formData.interested.filter(i => i !== option);

                                                setFormData(prev => ({
                                                    ...prev,
                                                    interested: newInterests
                                                }));

                                                setErrors({ ...errors, interested: '' });
                                            }}
                                            name={option}
                                            classes={{ root: classes.checkboxRoot }}
                                        />
                                    }
                                    label={option}
                                    classes={{ label: classes.checkboxLabel }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    {errors.interested && (
                        <FormHelperText style={{ fontFamily: font }}>{errors.interested}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };

    const renderMessageField = () => {
        if (settings.messageTitle) {
            return (
                <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                        <Typography variant="subtitle1" className={classes.fontFamily}>
                            {settings.messageTitle}
                        </Typography>
                        <TextareaAutosize
                            id="message"
                            name="message"
                            className={classes.textArea}
                            onChange={handleChange}
                            style={{
                                width: '100%',
                                fontFamily: font,
                                padding: 8,
                                borderRadius: 4,
                                border: `1px solid ${fontColor}`,
                                color: fontColor,
                                boxSizing: 'border-box',
                                minHeight: 55,
                                backgroundColor: backgroundColor
                            }}
                        />
                    </FormControl>
                </Grid>
            );
        }
    };

    const renderMarketingField = () => {
        return (
            <Grid item xs={12}>
                <FormControl component="fieldset">
                    <Typography variant="subtitle1" className={classes.fontFamily}>
                        {settings?.marketingText}
                    </Typography>
                    <FormGroup row>
                        {Object.values(NOTIFICATION_TYPES)
                            .reverse()
                            .map(option => (
                                <FormControlLabel
                                    key={option}
                                    control={
                                        <Checkbox
                                            checked={formData.marketingTypes.includes(option)}
                                            onChange={e => {
                                                const newMarketingTypes = e.target.checked
                                                    ? [...formData.marketingTypes, option]
                                                    : formData.marketingTypes.filter(i => i !== option);

                                                setFormData(prev => ({
                                                    ...prev,
                                                    marketingTypes: newMarketingTypes
                                                }));
                                            }}
                                            name={option}
                                            classes={{ root: classes.checkboxRoot }}
                                        />
                                    }
                                    label={option}
                                    classes={{ label: classes.checkboxLabel }}
                                />
                            ))}
                    </FormGroup>
                </FormControl>
            </Grid>
        );
    };

    const renderMap = {
        title: renderTitleField,
        firstName: renderFirstNameField,
        email: renderEmailField,
        surname: renderSurnameField,
        gender: renderGenderField,
        dateOfBirth: renderDateOfBirthField,
        mobilePhone: renderMobilePhoneField,
        referralSource: renderReferralSourceField,
        interested: renderInterestedField
    };

    if (!decoded) {
        return '';
    }

    const googleFonts = `https://fonts.googleapis.com/css2?family=${font}&display=swap`;

    return leadSaved ? (
        <Container style={{ maxWidth: '100%', padding: '20px' }} ref={containerRef}>
            <link href={googleFonts} rel="stylesheet" />
            <Typography
                gutterBottom
                style={{ textAlign: 'center', fontFamily: font }}
                className={classes.fontFamily}
                dangerouslySetInnerHTML={{ __html: settings.thankYouText }}
            />
        </Container>
    ) : (
        <Container style={{ maxWidth: '100%', padding: '20px' }} ref={containerRef}>
            <link href={googleFonts} rel="stylesheet" />
            <Typography
                variant="h3"
                gutterBottom
                className={`${classes.title} ${classes.fontFamily}`}
                style={{ fontSize: 20 }}
            >
                {settings.title || 'Submit on enquiry'}
            </Typography>
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Grid container spacing={3}>
                    {settings.fields && settings.fields.map(field => (renderMap[field] ? renderMap[field]() : ''))}
                    {/*{renderReferralSourceField()}*/}
                    {renderMessageField()}
                    {renderMarketingField()}
                    {settings.footerText && (
                        <Typography
                            style={{ padding: '0 12px', fontSize: 12 }}
                            dangerouslySetInnerHTML={{ __html: decodeURIComponent(settings.footerText) }}
                            className={classes.fontFamily}
                        />
                    )}
                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            style={buttonStyles}
                            className={classes.fontFamily}
                        >
                            Submit
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
}

export default Lead;
